/**
 * Admin V3 — Module declaration table
 *
 * Single source of truth for:
 *   - Sidebar navigation (AdminV3Sidebar consumes this)
 *   - Route registration sanity checking (AdminV3 consumes this implicitly)
 *
 * Schema:
 *   {
 *     id:       canonical module identifier (matches placeholder.moduleKey)
 *     route:    URL path under /admin-v3 (no leading slash)
 *     labelKey: i18n key (resolved via t('nav.<labelKey>'))
 *     icon:     lucide-react component
 *     group:    'operations' | 'financial' | 'configuration'
 *     subItems: optional array of nested {id, route, labelKey, icon}
 *   }
 */

import {
  LayoutDashboard, Calendar, Car, UserPlus, Activity, AlertTriangle,
  FileText, Building2, Euro, Percent, CreditCard, Settings, Mail,
  History, Bell, Send, ShieldCheck,
} from 'lucide-react';

export const MODULES = [
  // ============ OPERATIONS ============
  {
    id:       'overview',
    route:    'overview',
    labelKey: 'overview',
    icon:     LayoutDashboard,
    group:    'operations',
  },
  {
    id:       'bookings',
    route:    'bookings',
    labelKey: 'bookings',
    icon:     Calendar,
    group:    'operations',
    // P1-B3-2: bookings promoted to a collapsible parent — the read-only list
    // plus the migrated unassigned dispatch queue (auto-assign + manual assign).
    subItems: [
      { id: 'bookings.list',       route: 'bookings',            labelKey: 'bookings.list'       },
      { id: 'bookings.unassigned', route: 'bookings/unassigned', labelKey: 'bookings.unassigned' },
    ],
  },
  {
    id:       'drivers',
    route:    'drivers',
    labelKey: 'drivers',
    icon:     Car,
    group:    'operations',
  },
  {
    id:       'onboarding',
    route:    'onboarding',
    labelKey: 'onboarding',
    icon:     UserPlus,
    group:    'operations',
  },
  {
    id:       'operations',
    route:    'operations',
    labelKey: 'operations',
    icon:     Activity,
    group:    'operations',
    subItems: [
      { id: 'operations.live',          route: 'operations',                labelKey: 'operations.live'          },
      { id: 'operations.audit',         route: 'operations/audit',          labelKey: 'operations.audit'         },
      { id: 'operations.notifications', route: 'operations/notifications',  labelKey: 'operations.notifications' },
      { id: 'operations.health',        route: 'operations/health',         labelKey: 'operations.health'        },
    ],
  },
  {
    id:       'incidents',
    route:    'incidents',
    labelKey: 'incidents',
    icon:     AlertTriangle,
    group:    'operations',
  },
  // P1-B3-1: Contacts/messages migrated from legacy V1 (read + status triage).
  {
    id:       'contacts',
    route:    'contacts',
    labelKey: 'contacts',
    icon:     Mail,
    group:    'operations',
  },
  // Driver Compliance Domain — Phase 4 G5 (Plan §2.2 M-F-13..M-F-16).
  // Cross-driver hub + activity log under "operations" group.
  // Admin V3 remains the SINGLE AUTHORITATIVE administrative surface.
  {
    id:       'compliance',
    route:    'compliance',
    labelKey: 'compliance',
    icon:     ShieldCheck,
    group:    'operations',
    subItems: [
      { id: 'compliance.dashboard', route: 'compliance',          labelKey: 'compliance.dashboard' },
      { id: 'compliance.activity',  route: 'compliance/activity', labelKey: 'compliance.activity'  },
    ],
  },

  // ============ FINANCIAL ============
  // Facturation = the full commercial/fiscal domain, grouped as a single
  // collapsible parent: Devis -> Bon de commande -> Facture -> Avoir lifecycle.
  {
    id:       'billing',
    route:    'billing',
    labelKey: 'billing',
    icon:     FileText,
    group:    'financial',
    subItems: [
      { id: 'billing.invoices', route: 'billing/invoices', labelKey: 'billing.invoices' },
      { id: 'billing.avoirs',   route: 'billing/avoirs',   labelKey: 'billing.avoirs'   },
      { id: 'billing.devis',    route: 'billing/devis',    labelKey: 'billing.devis',    badgeKey: 'accepted_devis' },
      { id: 'billing.commande', route: 'billing/commande', labelKey: 'billing.commande', badgeKey: 'draft_bdc' },
      // AV3 A-13: billing.cycle + billing.vat removed — Category B (Billing/VAT).
    ],
  },
  {
    id:       'partners',
    route:    'partners',
    labelKey: 'partners',
    icon:     Building2,
    group:    'financial',
  },
  {
    id:       'corporate',
    route:    'corporate',
    labelKey: 'corporate',
    icon:     Building2,
    group:    'financial',
  },
  {
    id:       'financial',
    route:    'financial',
    labelKey: 'financial',
    icon:     Euro,
    group:    'financial',
    subItems: [
      { id: 'financial.payouts',     route: 'financial/payouts',     labelKey: 'financial.payouts'     },
      { id: 'financial.withdrawals', route: 'financial/withdrawals', labelKey: 'financial.withdrawals' },
      { id: 'financial.ledger',      route: 'financial/ledger',      labelKey: 'financial.ledger'      },
    ],
  },
  // AV3 release: commissions nav removed — Category B (the rate-management
  // API /admin/commissions/rates is not implemented; the read-only viewer
  // would 404. Route also removed from AdminV3.js so no dead/404 surface is
  // exposed — a direct URL falls through to the notFound catch-all).
  // AV3 A-13: subscriptions nav removed — Category C (dormant by design;
  // SubscriptionsDisabledPage remains reachable by direct URL only).

  // ============ CONFIGURATION ============
  {
    id:       'pricing',
    route:    'pricing',
    labelKey: 'pricing',
    icon:     Settings,
    group:    'configuration',
  },
  // AV3 A-13: messages nav removed (contact viewer deprioritized, non-daily-ops);
  // settings nav removed — platform-config → Category B (Commissions HOLD).
];

export const GROUP_ORDER = ['operations', 'financial', 'configuration'];
export const GROUP_LABEL_KEYS = {
  operations:    'nav.group.operations',
  financial:     'nav.group.financial',
  configuration: 'nav.group.configuration',
};
